/**
 * Social Proof to TreeNode Converter
 *
 * Converts social proof entities (Reviews, Ratings, StudyCases, BigNumbers)
 * to tree structure for rc-tree, grouped by category.
 */

import type { Review, Rating, StudyCase, BigNumber } from '@segmently/schema-models/entities';
import type { SocialProofTreeNode } from './types';

/**
 * Category labels for display
 */
const CATEGORY_LABELS: Record<string, string> = {
  review: 'Reviews',
  rating: 'Ratings',
  studyCase: 'Study Cases',
  bigNumber: 'Big Numbers',
};

/**
 * Builds a display label for a Review entity
 */
function reviewLabel(review: Review): string {
  const stars = review.rating ? `★${review.rating}` : '';
  const nickname = review.nickname || 'Anonymous';
  const text = review.reviewtext
    ? review.reviewtext.substring(0, 60) + (review.reviewtext.length > 60 ? '...' : '')
    : '';
  return `${stars} ${nickname}: ${text}`.trim();
}

/**
 * Builds a display label for a Rating entity
 */
function ratingLabel(rating: Rating): string {
  const platform = rating.platform ? rating.platform.toUpperCase() : '';
  const ratingVal = rating.rating || '';
  const count = rating.ratingCount ? `(${rating.ratingCount} reviews)` : '';
  return `${platform}: ${ratingVal} ${count}`.trim();
}

/**
 * Builds a display label for a StudyCase entity
 */
function studyCaseLabel(studyCase: StudyCase): string {
  const name = studyCase.featureName || 'Unnamed study';
  const outputCount = studyCase.outputs?.length ?? 0;
  const suffix = outputCount > 0 ? ` — ${outputCount} output${outputCount > 1 ? 's' : ''}` : '';
  return `${name}${suffix}`;
}

/**
 * Builds a display label for a BigNumber entity
 */
function bigNumberLabel(bigNumber: BigNumber): string {
  const amount = bigNumber.amount || '';
  const desc = bigNumber.description
    ? bigNumber.description.substring(0, 50) + (bigNumber.description.length > 50 ? '...' : '')
    : '';
  return `${amount} ${desc}`.trim();
}

/**
 * Converts all social proof entities to a tree structure grouped by category.
 *
 * Tree layout:
 * ```
 * [x] Reviews (3)
 *   [x] ★5 John: Great app!
 *   [x] ★4 Jane: Changed my life
 * [x] Ratings (2)
 *   [x] IOS: 4.8 (10K reviews)
 * [x] Study Cases (1)
 *   [x] Weight Loss Feature — 3 outputs
 * [x] Big Numbers (2)
 *   [x] 1M+ Monthly Active Users
 * ```
 */
export function socialProofToTreeNodes(
  reviews: Review[],
  ratings: Rating[],
  studyCases: StudyCase[],
  bigNumbers: BigNumber[],
): SocialProofTreeNode[] {
  const categories: SocialProofTreeNode[] = [];

  // Reviews category
  if (reviews.length > 0) {
    const reviewChildren: SocialProofTreeNode[] = reviews.map((review) => ({
      key: `review-${review.id}`,
      title: reviewLabel(review),
      nodeType: 'item' as const,
      category: 'review' as const,
      entityId: review.id,
      checkable: true,
      selectable: false,
    }));

    categories.push({
      key: 'cat-review',
      title: `${CATEGORY_LABELS.review} (${reviews.length})`,
      nodeType: 'category' as const,
      category: 'review' as const,
      checkable: true,
      selectable: false,
      children: reviewChildren,
    });
  }

  // Ratings category
  if (ratings.length > 0) {
    const ratingChildren: SocialProofTreeNode[] = ratings.map((rating) => ({
      key: `rating-${rating.id}`,
      title: ratingLabel(rating),
      nodeType: 'item' as const,
      category: 'rating' as const,
      entityId: rating.id,
      checkable: true,
      selectable: false,
    }));

    categories.push({
      key: 'cat-rating',
      title: `${CATEGORY_LABELS.rating} (${ratings.length})`,
      nodeType: 'category' as const,
      category: 'rating' as const,
      checkable: true,
      selectable: false,
      children: ratingChildren,
    });
  }

  // Study Cases category
  if (studyCases.length > 0) {
    const studyCaseChildren: SocialProofTreeNode[] = studyCases.map((sc) => ({
      key: `studyCase-${sc.id}`,
      title: studyCaseLabel(sc),
      nodeType: 'item' as const,
      category: 'studyCase' as const,
      entityId: sc.id,
      checkable: true,
      selectable: false,
    }));

    categories.push({
      key: 'cat-studyCase',
      title: `${CATEGORY_LABELS.studyCase} (${studyCases.length})`,
      nodeType: 'category' as const,
      category: 'studyCase' as const,
      checkable: true,
      selectable: false,
      children: studyCaseChildren,
    });
  }

  // Big Numbers category
  if (bigNumbers.length > 0) {
    const bigNumberChildren: SocialProofTreeNode[] = bigNumbers.map((bn) => ({
      key: `bigNumber-${bn.id}`,
      title: bigNumberLabel(bn),
      nodeType: 'item' as const,
      category: 'bigNumber' as const,
      entityId: bn.id,
      checkable: true,
      selectable: false,
    }));

    categories.push({
      key: 'cat-bigNumber',
      title: `${CATEGORY_LABELS.bigNumber} (${bigNumbers.length})`,
      nodeType: 'category' as const,
      category: 'bigNumber' as const,
      checkable: true,
      selectable: false,
      children: bigNumberChildren,
    });
  }

  return categories;
}
